import { ElementType, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
// guards
import useAuth from "../hooks/useAuth";
// layouts
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";

import LoadingScreen from "../components/LoadingScreen";
import WebShopWrapper from "../pages/webshop/Wrapper";
import * as Sentry from "@sentry/react"
// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { isAuthenticated } = useAuth();

    const isDashboard = pathname.includes("/dashboard") && isAuthenticated;

    return (
        <Suspense fallback={<LoadingScreen isDashboard={isDashboard}/>}>
            <Component {...props} />
        </Suspense>
    );
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

export default function Router() {
    return (
        <SentryRoutes>
            <Route
                path="bxwm2"
            >
                <Route path="auth/login" element={<GuestGuard><Login/></GuestGuard>}/>
                <Route path="logout" element={<Logout/>}/>

                <Route
                    path="webshop"
                    element={
                        <AuthGuard>
                            <WebShopWrapper>
                                <DashboardLayout/>
                            </WebShopWrapper>
                        </AuthGuard>
                    }>
                    <Route path="" element={<Navigate to={"products"}/>}/>
                    <Route path="products" element={<ProductsPage/>}/>
                    <Route path="basket" element={<BasketPage/>}/>
                    <Route path="history" element={<HistoryPage/>}/>
                </Route>

                <Route
                    path=""
                    element={
                        <AuthGuard>
                            <DashboardLayout/>
                        </AuthGuard>
                    }
                >
                    <Route path="home" element={<Home/>}/>
                    <Route path="" element={<Navigate to="home" replace/>}/>
                    <Route path="labels/*" element={<LabelsPage/>}/>
                    <Route path="stockcount/*" element={<StockCountPage/>}/>
                    <Route path="monitor/*" element={<MonitorPage/>}/>
                    <Route path="reports/*" element={<GenericReportPage/>}/>
                    <Route path="assets/manager" element={<AssetsManagerPage/>}/>
                    <Route path="deliverymanager/*" element={<DeliveryManagerPage/>}/>
                    <Route path="plugins/raumaullvare" element={<RaumaUllevarePage/>}/>
                    <Route path="plugins/norgescatering" element={<NorgesCateringPage/>}/>
                </Route>

                <Route path="*" element={<LogoOnlyLayout/>}

                >
                    <Route path="500" element={<Page500/>}/>
                    <Route path="*" element={<Page404/>}/>
                </Route>
            </Route>
        </SentryRoutes>
    )
}

// Pages
const Home = Loadable(lazy(() => import("../pages/home/Home")));
const Logout = Loadable(lazy(() => import("../pages/auth/Logout")));
const LabelsPage = Loadable(lazy(() => import("../pages/labels/LabelsPage")));
const MonitorPage = Loadable(lazy(() => import("../pages/monitor/MonitorPage")));
const StockCountPage = Loadable(lazy(() => import("../pages/stockcount/StockCountPage")));
const ProductsPage = Loadable(lazy(() => import("../pages/webshop/ProductsPage")));
const BasketPage = Loadable(lazy(() => import("../pages/webshop/BasketPage")));
const HistoryPage = Loadable(lazy(() => import("../pages/webshop/HistoryPage")));
const GenericReportPage = Loadable(lazy(() => import("../pages/reports/GenericReportPage")));
const AssetsManagerPage = Loadable(lazy(() => import("../pages/assets/AssetsPage")));
const DeliveryManagerPage = Loadable(lazy(() => import("../pages/deliverymanager/DeliveryManagerPage")));

// Custom Plugins/Pages
const RaumaUllevarePage = Loadable(lazy(() => import("../pages/_custom/rauma/RaumaUllvarePage")));
const NorgesCateringPage = Loadable(lazy(() => import("../pages/_custom/norgescatering/NorgesCateringPage")));

// AUTHENTICATION
const Login = Loadable(lazy(() => import("../pages/auth/Login")));

const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const Page404 = Loadable(lazy(() => import("../pages/Page404")));
